import React from "react";
import { useSimulator } from "@/context/SimulatorContext";
import { Card } from "@/components/ui/card";
import { NumberField } from "@/components/simulator/NumberField";
import { fmtEUR, fmtPct } from "@/lib/format";
import { Receipt, Zap, FlaskConical, Users, Wrench } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";

const CATEGORIES = [
  { key: "energy", label: "Energia & Climatizzazione", color: "#ef4444", icon: Zap, testId: "opex-energy" },
  { key: "consumables", label: "Consumabili (Nutrienti, CO₂)", color: "#f59e0b", icon: FlaskConical, testId: "opex-consumables" },
  { key: "labor", label: "Manodopera & Agronomi", color: "#6366f1", icon: Users, testId: "opex-labor" },
  { key: "maintenance", label: "Manutenzione & Sensori", color: "#14b8a6", icon: Wrench, testId: "opex-maintenance" },
];

export const OpexTab = () => {
  const { inputs, summary, updateOpex } = useSimulator();
  const total = summary.opex.monthly;
  const hasFutureLots = summary.futureLots.count > 0;

  const chartData = CATEGORIES.map((c) => ({
    name: c.label,
    value: inputs.opex[c.key],
    color: c.color,
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-4">
        <div className="rounded-xl bg-rose-500/10 border border-rose-500/30 p-3">
          <Receipt className="h-7 w-7 text-rose-400" />
        </div>
        <div>
          <h2 className="font-display text-2xl font-bold text-slate-100">
            Costi Operativi Mensili (OPEX)
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Struttura fissa dei costi ricorrenti sulle operazioni di
            coltivazione e trasformazione.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 bg-slate-900/70 border-slate-800 p-6 space-y-6">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.key} className="flex items-start gap-4">
                <div
                  className="rounded-lg p-2.5 border"
                  style={{ borderColor: `${c.color}55`, background: `${c.color}15` }}
                >
                  <Icon className="h-4 w-4" style={{ color: c.color }} />
                </div>
                <div className="flex-1">
                  <NumberField
                    label={c.label}
                    value={inputs.opex[c.key]}
                    onChange={(v) => updateOpex({ [c.key]: v })}
                    min={0}
                    max={50000}
                    step={50}
                    unit="€ / mese"
                    testId={c.testId}
                    sliderTheme="violet-slider"
                  />
                </div>
              </div>
            );
          })}
        </Card>

        <Card className="bg-slate-900/70 border-slate-800 p-6 flex flex-col">
          <h3 className="font-display text-lg font-semibold text-slate-100 mb-2">
            Ripartizione OPEX
          </h3>
          <div className="flex-1 min-h-[240px]">
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={2}
                  stroke="#0b0f19"
                >
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v) => fmtEUR(v) + " / mese"}
                  contentStyle={{ background: "#0b0f19", border: "1px solid #1f2937", borderRadius: 8 }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11, color: "#94a3b8" }}
                  iconSize={8}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="pt-4 border-t border-slate-800 space-y-2">
            {CATEGORIES.map((c) => {
              const val = inputs.opex[c.key];
              const pct = total > 0 ? (val / total) * 100 : 0;
              return (
                <div key={c.key} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: c.color }} />
                    <span className="text-slate-400">{c.label}</span>
                  </div>
                  <span className="font-mono-tab text-slate-300">{fmtPct(pct)}</span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card
          data-testid="opex-total-monthly-display"
          className="bg-gradient-to-br from-rose-500/15 to-rose-600/5 border border-rose-500/30 p-5"
        >
          <div className="text-xs font-semibold tracking-wider uppercase text-rose-300">
            OPEX Mensile (Lotti 1-2)
          </div>
          <div className="mt-2 font-display font-extrabold text-3xl text-rose-300 font-mono-tab">
            {fmtEUR(summary.opex.monthly)}
          </div>
          {hasFutureLots && (
            <div className="mt-1 text-[11px] text-slate-500 font-mono-tab">
              + Lotti futuri: {fmtEUR(summary.futureLots.opex / 12)} / mese
            </div>
          )}
        </Card>
        <Card
          data-testid="opex-total-annual-display"
          className="bg-gradient-to-br from-rose-500/15 to-rose-600/5 border border-rose-500/30 p-5"
        >
          <div className="text-xs font-semibold tracking-wider uppercase text-rose-300">
            OPEX Annuo Netto (post energia)
          </div>
          <div className="mt-2 font-display font-extrabold text-3xl text-rose-300 font-mono-tab">
            {fmtEUR(summary.totalOpexAnnual)}
          </div>
          <div className="mt-1 text-[11px] text-slate-500 font-mono-tab space-y-0.5">
            <div>base: {fmtEUR(summary.opex.annual)} + lotti: {fmtEUR(summary.futureLots.opex)}</div>
            {summary.energySavings > 0 && (
              <div className="text-emerald-400">− risparmio FV: {fmtEUR(summary.energySavings)}</div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};
